import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MdAttachFile,
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdDownload,
  MdEvent,
  MdEventBusy,
  MdEventNote,
  MdExpandMore,
  MdPendingActions,
  MdPercent,
  MdPlace,
  MdSchedule,
  MdTaskAlt,
} from "react-icons/md";
import dayjs from "dayjs";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { formatDate, formatNumber, formatTime, formatWeekday } from "utils/format";
import Button, { IconButton } from "components/button";
import Card from "components/card";
import Badge from "components/badge/Badge";
import Skeleton from "components/skeleton/Skeleton";
import StatCard from "components/stat-card";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";

const MENU_ITEM =
  "focus-ring flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-fg transition-colors hover:bg-surface-subtle";

// Helper function to generate calendar event data
const generateCalendarLinks = (service, eventDate) => {
  const startDateTime = dayjs(`${eventDate.format("YYYY-MM-DD")}T${service.startAt}`);
  const endDateTime = dayjs(`${eventDate.format("YYYY-MM-DD")}T${service.endAt}`);

  const title = encodeURIComponent(service.name);
  const description = encodeURIComponent(service.description || "Service assignment");
  const location = encodeURIComponent(service.address || "");

  const googleStart = startDateTime.format("YYYYMMDDTHHmmss");
  const googleEnd = endDateTime.format("YYYYMMDDTHHmmss");
  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${googleStart}/${googleEnd}&details=${description}&location=${location}`;

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `DTSTART:${startDateTime.format("YYYYMMDDTHHmmss")}`,
    `DTEND:${endDateTime.format("YYYYMMDDTHHmmss")}`,
    `SUMMARY:${service.name}`,
    `DESCRIPTION:${service.description || "Service assignment"}`,
    `LOCATION:${service.address || ""}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n");

  return { googleUrl, icsContent };
};

// Service start/end values are times of day ("09:30:00").
const serviceTime = (time) => formatTime(`2000-01-01T${time}`);

const VolunteerServices = () => {
  const [queryParams] = useSearchParams();
  const token = queryParams.get("token");
  const [services, setServices] = useState([]);
  const [user, setUser] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [openCalendarDropdown, setOpenCalendarDropdown] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [loadingAction, setLoadingAction] = useState(null); // Track loading state for actions
  const [showPastServices, setShowPastServices] = useState(false); // Collapsible past services
  const serviceRefs = useRef({}); // Refs for smooth scrolling

  const fetchServices = useCallback(
    () =>
      AtriumApi.get("/v1/volunteer/services", { params: { token } }).then((resp) => {
        setUser(resp?.data?.user || {});
        setServices(resp?.data?.services || []);
        setStats(resp?.data?.stats);
      }),
    [token]
  );

  const loadServices = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchServices()
      .catch((error) => setLoadError(error))
      .finally(() => setLoading(false));
  }, [fetchServices]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const refreshServices = () =>
    fetchServices().catch((error) => {
      console.error("Failed to refresh services:", error);
    });

  const setState = (userServiceId, newState) => {
    setLoadingAction(`${userServiceId}-${newState}`);
    return AtriumApi.put(
      `/v1/volunteer/services/${userServiceId}`,
      { state: newState },
      { params: { token } }
    )
      .then(() => {
        notify.success(newState === "confirmed" ? "Service accepted" : "Service declined");
        // Refresh the services list to show updated data and apply proper filtering
        return refreshServices();
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not update service"));
      })
      .finally(() => {
        setLoadingAction(null);
      });
  };

  const downloadICS = (icsContent, serviceName) => {
    const blob = new Blob([icsContent], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${serviceName.replace(/[^a-z0-9]/gi, "_")}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setOpenCalendarDropdown(null);
    notify.success("Calendar event downloaded");
  };

  const downloadAttachment = async (service, attachment, originalIdx) => {
    try {
      const response = await AtriumApi.get(
        `/v1/volunteer/services/${service.id}/attachments/${originalIdx}/download`,
        {
          params: { token },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename || "attachment");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      notify.success("Download started");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not download attachment"));
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openCalendarDropdown && !event.target.closest(".calendar-dropdown-container")) {
        setOpenCalendarDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCalendarDropdown]);

  // Smooth scroll to expanded service
  useEffect(() => {
    if (selectedService && serviceRefs.current[selectedService.id]) {
      setTimeout(() => {
        serviceRefs.current[selectedService.id]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    }
  }, [selectedService]);

  // Calendar helpers
  const getDaysInMonth = () => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const days = [];

    const startDay = startOfMonth.day();
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= endOfMonth.date(); i++) {
      days.push(currentMonth.date(i));
    }

    return days;
  };

  const getServicesForDate = (date) => {
    if (!date) return [];
    return services.filter((s) => {
      // Don't use UTC conversion for date-only values to avoid timezone shifts
      const serviceDate = dayjs(s.service.date);
      return serviceDate.isSame(date, "day");
    });
  };

  // Relative labels for the coming and past week, otherwise the standard date.
  const formatSmartDate = (dateStr) => {
    // Don't use UTC conversion for date-only values to avoid timezone shifts
    const date = dayjs(dateStr);
    const today = dayjs();
    const tomorrow = dayjs().add(1, "day");

    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(tomorrow, "day")) return "Tomorrow";
    if (date.isSame(today.subtract(1, "day"), "day")) return "Yesterday";

    if (date.isAfter(today) && date.isBefore(today.add(7, "day"))) {
      return `Next ${formatWeekday(date, "long")}`;
    }

    if (date.isBefore(today) && date.isAfter(today.subtract(7, "day"))) {
      return `Last ${formatWeekday(date, "long")}`;
    }

    return formatDate(dateStr);
  };

  // Separate services into upcoming and past
  const today = dayjs().startOf("day");

  const upcomingServices = services.filter((s) => {
    if (!s?.service?.date) return false;
    // Don't use UTC conversion for date-only values to avoid timezone shifts
    const serviceDate = dayjs(s.service.date).startOf("day");
    return serviceDate.isSame(today, "day") || serviceDate.isAfter(today, "day");
  });

  const pastServices = services.filter((s) => {
    if (!s?.service?.date) return false;
    // Don't use UTC conversion for date-only values to avoid timezone shifts
    const serviceDate = dayjs(s.service.date).startOf("day");
    return serviceDate.isBefore(today, "day");
  });

  const ServiceCard = ({ userService, isExpanded }) => {
    const service = userService.service;
    // Don't use UTC conversion for date-only values to avoid timezone shifts
    const eventDate = dayjs(service.date);
    const state = userService.state;
    const calendarOpen = openCalendarDropdown === userService.id;
    const attachments = (service.attachments || [])
      .map((att, originalIdx) => ({ att, originalIdx }))
      .filter(({ att }) => att && att.url && att.filename);

    const statusBadge =
      state === "confirmed" ? (
        <Badge tone="success">
          <MdCheck aria-hidden="true" className="h-3.5 w-3.5" />
          Accepted
        </Badge>
      ) : state === "declined" ? (
        <Badge tone="danger">
          <MdClose aria-hidden="true" className="h-3.5 w-3.5" />
          Declined
        </Badge>
      ) : (
        <Badge tone="warning">
          <MdSchedule aria-hidden="true" className="h-3.5 w-3.5" />
          Pending
        </Badge>
      );

    return (
      <Card
        ref={(el) => (serviceRefs.current[userService.id] = el)}
        className={isExpanded ? "ring-2 ring-primary dark:ring-accent" : undefined}
      >
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setSelectedService(selectedService?.id === userService.id ? null : userService)}
          className="focus-ring flex w-full items-start justify-between gap-3 rounded-card p-4 text-left"
        >
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-semibold text-fg">{service.name}</span>
              <MdExpandMore
                aria-hidden="true"
                className={`h-5 w-5 shrink-0 text-fg-muted transition-transform duration-300 ${
                  isExpanded ? "rotate-180" : ""
                }`}
              />
            </div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-primary dark:text-accent">
              <MdSchedule aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span>
                {formatSmartDate(service.date)} · {serviceTime(service.startAt)} to{" "}
                {serviceTime(service.endAt)}
              </span>
            </div>
          </div>
          {statusBadge}
        </button>

        {isExpanded && (
          <div className="mx-4 flex flex-col gap-4 border-t border-line pb-4 pt-4">
            {service.description && (
              <p className="rounded-control bg-surface-subtle p-3 text-sm leading-relaxed text-fg-muted">
                {service.description}
              </p>
            )}

            {service.address && (
              <div className="flex items-start gap-2 text-sm text-fg-muted">
                <MdPlace aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{service.address}</span>
              </div>
            )}

            {attachments.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-semibold text-fg-muted">Attachments</h3>
                <ul className="flex flex-col gap-2">
                  {attachments.map(({ att: attachment, originalIdx }) => (
                    <li key={originalIdx}>
                      <Button
                        variant="secondary"
                        fullWidth
                        leftIcon={<MdAttachFile aria-hidden="true" className="h-4 w-4" />}
                        rightIcon={<MdDownload aria-hidden="true" className="h-4 w-4" />}
                        onClick={() => downloadAttachment(service, attachment, originalIdx)}
                      >
                        <span className="min-w-0 truncate">{attachment.filename || "Attachment"}</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="calendar-dropdown-container relative">
                <Button
                  variant="secondary"
                  fullWidth
                  aria-haspopup="menu"
                  aria-expanded={calendarOpen}
                  leftIcon={<MdEvent aria-hidden="true" className="h-4 w-4" />}
                  onClick={() => setOpenCalendarDropdown(calendarOpen ? null : userService.id)}
                >
                  Add to Calendar
                </Button>

                {calendarOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 z-20 mt-2 w-full overflow-hidden rounded-control border border-line bg-surface shadow-overlay"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className={MENU_ITEM}
                      onClick={() => {
                        const { googleUrl } = generateCalendarLinks(service, eventDate);
                        window.open(googleUrl, "_blank");
                        setOpenCalendarDropdown(null);
                      }}
                    >
                      <MdEvent aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                      Google Calendar
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={`${MENU_ITEM} border-t border-line`}
                      onClick={() => {
                        const { icsContent } = generateCalendarLinks(service, eventDate);
                        downloadICS(icsContent, service.name);
                      }}
                    >
                      <MdDownload aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                      Apple / Outlook
                    </button>
                  </div>
                )}
              </div>

              {state === "na" && (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<MdClose aria-hidden="true" className="h-4 w-4" />}
                    loading={loadingAction === `${userService.id}-declined`}
                    onClick={() => setState(userService.id, "declined")}
                  >
                    Decline
                  </Button>
                  <Button
                    leftIcon={<MdCheck aria-hidden="true" className="h-4 w-4" />}
                    loading={loadingAction === `${userService.id}-confirmed`}
                    onClick={() => setState(userService.id, "confirmed")}
                  >
                    Accept
                  </Button>
                </div>
              )}

              {state === "confirmed" && (
                <Button
                  variant="secondary"
                  fullWidth
                  loading={loadingAction === `${userService.id}-declined`}
                  onClick={() => setState(userService.id, "declined")}
                >
                  Change to Decline
                </Button>
              )}

              {state === "declined" && (
                <Button
                  variant="secondary"
                  fullWidth
                  loading={loadingAction === `${userService.id}-confirmed`}
                  onClick={() => setState(userService.id, "confirmed")}
                >
                  Change to Accept
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  const header = (
    <PageHeader
      title="Services"
      description={
        user?.firstName ? `Welcome back, ${user.firstName} ${user.lastName || ""}`.trim() : undefined
      }
    />
  );

  if (loading) {
    return (
      <PageContainer>
        {header}
        <div className="flex flex-col gap-4 lg:flex-row-reverse">
          <Skeleton className="h-72 w-full rounded-card lg:w-72" />
          <div className="flex flex-1 flex-col gap-3">
            <Skeleton className="h-24 w-full rounded-card" />
            <Skeleton className="h-20 w-full rounded-card" />
            <Skeleton className="h-20 w-full rounded-card" />
          </div>
        </div>
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        {header}
        <ErrorState
          message={getApiErrorMessage(loadError, "We couldn’t load your services.")}
          onRetry={loadServices}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}
      <div className="flex flex-col gap-4 lg:flex-row-reverse">
        {/* Calendar - Mobile: below content, Desktop: right sidebar */}
        <div className="order-2 w-full lg:order-1 lg:w-72 lg:shrink-0">
          <Card padding="sm" className="lg:sticky lg:top-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-fg">{currentMonth.format("MMMM YYYY")}</h2>
              <div className="flex items-center gap-1">
                <IconButton
                  size="sm"
                  icon={<MdChevronLeft className="h-5 w-5" />}
                  label="Previous month"
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}
                />
                <Button size="sm" variant="secondary" onClick={() => setCurrentMonth(dayjs())}>
                  Today
                </Button>
                <IconButton
                  size="sm"
                  icon={<MdChevronRight className="h-5 w-5" />}
                  label="Next month"
                  onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}
                />
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1">
              {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
                <div key={idx} aria-hidden="true" className="py-1 text-center text-2xs font-bold text-fg-muted">
                  {day}
                </div>
              ))}

              {getDaysInMonth().map((day, idx) => {
                const dayServices = day ? getServicesForDate(day) : [];
                const isToday = day && day.isSame(dayjs(), "day");
                const hasServices = dayServices.length > 0;

                return (
                  <div key={idx} className="aspect-square">
                    {day && (
                      <div
                        className={`relative flex h-full w-full items-center justify-center rounded-control text-xs font-medium ${
                          isToday
                            ? "bg-primary font-bold text-primary-fg"
                            : hasServices
                            ? "bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200"
                            : "text-fg"
                        }`}
                      >
                        {day.date()}
                        {hasServices && (
                          <span className="sr-only">
                            , {dayServices.length} scheduled
                          </span>
                        )}
                        {hasServices && !isToday && (
                          <span
                            aria-hidden="true"
                            className="absolute bottom-0.5 h-1 w-1 rounded-full bg-primary dark:bg-accent"
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Services List - Mobile: top, Desktop: left side (main content) */}
        <div className="order-1 flex min-w-0 flex-1 flex-col gap-3 lg:order-2">
          {stats && stats.totalServices > 0 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatCard
                icon={MdTaskAlt}
                accent="sage"
                label="Completed"
                value={formatNumber(stats.confirmedCount)}
              />
              <StatCard
                icon={MdPercent}
                accent="sky"
                label="Response Rate"
                value={`${formatNumber(stats.responseRate)}%`}
              />
              <StatCard
                icon={MdPendingActions}
                accent="gold"
                label="Pending"
                value={formatNumber(stats.pendingCount)}
              />
            </div>
          )}

          {upcomingServices.length > 0 && (
            <section className="flex flex-col gap-3">
              <div className="flex items-center justify-between px-1">
                <h2 className="text-heading">Upcoming Services</h2>
                <Badge>{upcomingServices.length}</Badge>
              </div>
              {upcomingServices.map((userService) => (
                <ServiceCard
                  key={userService.id}
                  userService={userService}
                  isExpanded={selectedService?.id === userService.id}
                />
              ))}
            </section>
          )}

          {pastServices.length > 0 && (
            <section className="flex flex-col gap-3">
              <h2>
                <button
                  type="button"
                  aria-expanded={showPastServices}
                  onClick={() => setShowPastServices(!showPastServices)}
                  className="focus-ring flex w-full items-center justify-between rounded-control px-1 py-3 text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className="text-sm font-bold text-fg-muted">Past Services</span>
                    <Badge>{pastServices.length}</Badge>
                  </span>
                  <MdExpandMore
                    aria-hidden="true"
                    className={`h-5 w-5 text-fg-muted transition-transform duration-300 ${
                      showPastServices ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </h2>

              {showPastServices && (
                <div className="flex flex-col gap-3">
                  {pastServices.slice(0, 10).map((userService) => (
                    <div
                      key={userService.id}
                      className="opacity-75 transition-opacity duration-200 hover:opacity-100"
                    >
                      <ServiceCard
                        userService={userService}
                        isExpanded={selectedService?.id === userService.id}
                      />
                    </div>
                  ))}
                  {pastServices.length > 10 && (
                    <Button
                      variant="ghost"
                      fullWidth
                      onClick={() => notify.info("Full history view coming soon")}
                    >
                      View All {pastServices.length} Past Services
                    </Button>
                  )}
                </div>
              )}
            </section>
          )}

          {services.length > 0 && upcomingServices.length === 0 && pastServices.length === 0 && (
            <Card padding="md">
              <EmptyState
                icon={MdEventBusy}
                title="Services Missing Dates"
                description={`${services.length} assigned service${
                  services.length === 1 ? " doesn’t" : "s don’t"
                } have a date yet.`}
              />
            </Card>
          )}

          {services.length === 0 && (
            <Card padding="md">
              <EmptyState
                icon={MdEventNote}
                title="No Services Assigned"
                description="When you’re scheduled to serve, your assignments will appear here."
              />
            </Card>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default VolunteerServices;
